import { Box, Button, Container, Paper, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useDeviceType } from "../../hooks/useDeviceType";
import styles from "../Chat/AnalysisPanel/AnalysisPanel.module.css";
import { useTranslation } from "react-i18next";
import { MdOutlineDescription } from "react-icons/md";
import IconWrapper from "./IconWrapper";

const Description = IconWrapper(MdOutlineDescription);

type DocumentViewerProps = {
    filePath: string;
    fileDownloadPath: string;
    downloadFileName: string;
    fileExtension: string;
};

const DocumentViewer = ({ filePath, fileDownloadPath, downloadFileName, fileExtension }: DocumentViewerProps) => {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "common" });
    const { isDesktop } = useDeviceType();

    const iframeTitle = useMemo(() => downloadFileName, [downloadFileName]);

    const handleOpen = () => {
        if (filePath) window.open(filePath ?? filePath, "_blank", "noopener,noreferrer");
    };

    if (isDesktop || fileExtension !== "pdf") {
        return <iframe className={styles.documentFrame} title={iframeTitle} src={filePath} width="100%" height="100%" style={{ border: "none" }} />;
    }

    // Mobile/Tablet View
    return (
        <Container maxWidth="sm" sx={{ py: 3 }}>
            <Paper
                sx={{
                    background: `transparent`,
                    borderRadius: "20px",
                    p: 0,
                    overflow: "hidden",
                    position: "relative"
                }}
            >
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        color: theme.palette.text.primary
                    }}
                >
                    <Box
                        sx={{
                            mb: 3,
                            display: "flex",
                            justifyContent: "center"
                        }}
                    >
                        <Box
                            sx={{
                                background: "rgba(255, 255, 255, 0.15)",
                                borderRadius: "20px",
                                p: 3,
                                backdropFilter: "blur(10px)",
                                border: `1px solid ${theme.palette.borderColor}`
                            }}
                        >
                            <Description
                                style={{
                                    fontSize: 48,
                                    color: theme.palette.primary.main
                                }}
                            />
                        </Box>
                    </Box>

                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            mb: 0,
                            fontSize: "1.3rem"
                        }}
                    >
                        {t("analysisPanel.openDocument")}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.disclaimerText,
                            mb: 4,
                            fontSize: "0.9rem",
                            fontWeight: 400
                        }}
                    >
                        {downloadFileName || iframeTitle}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleOpen}
                            sx={{
                                background: theme.palette.actionButtonBackgroundHover,
                                borderRadius: "50px",
                                py: 1.8,
                                px: 4,
                                fontSize: "1rem",
                                fontWeight: 600,
                                textTransform: "none",
                                color: theme.palette.text.primary,
                                border: `1px solid ${theme.palette.borderColor}`
                            }}
                        >
                            {t("analysisPanel.openFileNewWindow")}
                        </Button>

                        <Button
                            variant="outlined"
                            size="medium"
                            component="a"
                            href={fileDownloadPath}
                            download={downloadFileName}
                            sx={{
                                color: theme.palette.text.primary,
                                borderColor: theme.palette.borderColor,
                                borderRadius: "50px",
                                py: 1.5,
                                px: 4,
                                fontSize: "0.95rem",
                                fontWeight: 500,
                                textTransform: "none",
                                background: theme.palette.actionButtonBackground,
                                backdropFilter: "blur(10px)"
                            }}
                        >
                            {t("download")}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default DocumentViewer;
